import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  getRecentMaintenanceEvents,
  listMaintenanceTypes,
  maintenanceTypeLabel,
  WARTUNGEN_REALTIME_DOMAINS,
  type RecentMaintenanceEvent,
} from "@/lib/wartungen";
import { useRealtimeSync } from "@/lib/useRealtime";

const STATUS_LABELS: Record<string, string> = {
  open: "Offen",
  ok: "In Ordnung",
  defect_found: "Defekt festgestellt",
  defect_fixed: "Defekt behoben",
  skipped: "Übersprungen",
  cancelled: "Abgebrochen",
};

function statusLabel(value?: string | null): string {
  const key = String(value || "").trim();
  if (!key) return "—";
  if (STATUS_LABELS[key]) return STATUS_LABELS[key];
  return key
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const dateFormatter = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

function fmt(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : dateFormatter.format(d);
}

export function WartungenKategoriePage() {
  const [params] = useSearchParams();
  const categoryKey = (params.get("kategorie") || "").trim();

  const [categoryName, setCategoryName] = useState(categoryKey);
  const [events, setEvents] = useState<RecentMaintenanceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!categoryKey) {
      setLoading(false);
      setError("Keine Wartungskategorie angegeben.");
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [types, recent] = await Promise.all([
        listMaintenanceTypes().catch(() => []),
        getRecentMaintenanceEvents(categoryKey, 20).catch(() => [] as RecentMaintenanceEvent[]),
      ]);
      const active = types.find((t) => String(t.key || "").trim() === categoryKey);
      setCategoryName(active ? maintenanceTypeLabel(active) : categoryKey);
      setEvents(recent);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [categoryKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: [...WARTUNGEN_REALTIME_DOMAINS],
    enabled: !!categoryKey,
    onChange: () => void load({ silent: true }),
  });

  const statusText = useMemo(() => {
    if (loading) return "Lade letzte Wartungen …";
    if (!events.length) return "Noch keine Wartungen vorhanden.";
    return `Zuletzt ${events.length} Wartung${events.length === 1 ? "" : "en"}.`;
  }, [loading, events.length]);

  return (
    <AppShell
      title={categoryName || "Wartungskategorie"}
      subtitle={categoryKey ? `Schlüssel: ${categoryKey}` : undefined}
      back={{ to: "/wartungen/einstellungen", label: "zurück zu Kategorien" }}
      actions={
        categoryKey ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/wartungen/projekt-start?type_key=${encodeURIComponent(categoryKey)}`}
              className="inline-flex h-10 items-center rounded-lg bg-accent px-4 text-sm font-medium text-label-on-accent hover:bg-accent-hover"
            >
              Neue Wartung starten
            </Link>
            <Link
              to={`/wartungen/formulare?kategorie=${encodeURIComponent(categoryKey)}`}
              className="inline-flex h-10 items-center rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
            >
              Formulare verwalten
            </Link>
          </div>
        ) : null
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <section className="rounded-ios border border-separator bg-card shadow-ios">
        <header className="border-b border-separator px-5 py-4">
          <h2 className="text-base font-semibold text-label">Letzte Wartungen</h2>
          <p className="mt-1 text-sm text-label-secondary">{statusText}</p>
        </header>
        {loading ? (
          <div className="flex items-center gap-2 px-5 py-4 text-sm text-label-secondary">
            <Spinner /> Lade …
          </div>
        ) : events.length === 0 ? null : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-separator text-left text-xs uppercase tracking-wide text-label-tertiary">
                  <th className="px-4 py-2 font-medium">Datum</th>
                  <th className="px-4 py-2 font-medium">Objekt</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Bearbeiter</th>
                  <th className="px-4 py-2 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {events.map((row, idx) => {
                  const assetId = String(row.asset_id || "").trim();
                  const eventId = String(row.maintenance_event_id || "").trim();
                  const name = String(row.asset_name || "").trim();
                  const objectDisplay = name ? `${name} (${assetId || "—"})` : assetId || "—";
                  const open =
                    assetId && eventId
                      ? `/wartungen/form?type=${encodeURIComponent(String(row.type || "").trim())}&asset_id=${encodeURIComponent(assetId)}&maintenance_event_id=${encodeURIComponent(eventId)}`
                      : "";
                  const performer =
                    String(row.performed_by_name || row.performed_by || row.user || "").trim() || "—";
                  return (
                    <tr key={`${eventId}-${idx}`} className="border-b border-separator/60">
                      <td className="px-4 py-2">{fmt(row.executed_at || row.created_at || row.date)}</td>
                      <td className="px-4 py-2">{objectDisplay}</td>
                      <td className="px-4 py-2">{statusLabel(row.status)}</td>
                      <td className="px-4 py-2">{performer}</td>
                      <td className="px-4 py-2">
                        {open ? (
                          <Link
                            to={open}
                            className="inline-flex h-8 items-center rounded-lg bg-card px-3 text-xs font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
                          >
                            Öffnen
                          </Link>
                        ) : (
                          <span className="text-label-tertiary">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  );
}
